import { Link } from "react-router-dom";
import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";

const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M5 13l4 4L19 7" />
  </svg>
);

const SERVICES = [
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <circle cx="12" cy="12" r="10" />
        <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
    title: "Web Development",
    tagline: "React · Next.js · TypeScript",
    desc: "Blazing-fast, pixel-perfect web apps built with modern frameworks. From high-conversion landing pages to complex enterprise dashboards — optimized for Core Web Vitals and SEO from day one.",
    features: ["React & Next.js SPAs", "SEO-ready SSR/SSG", "E-Commerce & headless CMS", "Enterprise portals & dashboards", "Progressive Web Apps", "Core Web Vitals optimization"],
    href: "/services/web-development",
    color: "from-violet-500/20 to-violet-600/5",
    accent: "text-violet-400",
    border: "border-violet-500/20",
    gradient: "from-violet-600 to-indigo-600",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <rect x="5" y="2" width="14" height="20" rx="2" />
        <path d="M12 18h.01" />
      </svg>
    ),
    title: "Mobile Development",
    tagline: "iOS · Android · React Native · Flutter",
    desc: "Native and cross-platform mobile apps that feel at home on every device. We handle everything from architecture to App Store submission and ongoing crash monitoring.",
    features: ["Native iOS (Swift/SwiftUI)", "Native Android (Kotlin)", "React Native cross-platform", "Flutter cross-platform", "App Store & Play Store submission", "Push notifications & deep linking"],
    href: "/services/mobile-apps",
    color: "from-cyan-500/20 to-cyan-600/5",
    accent: "text-cyan-400",
    border: "border-cyan-500/20",
    gradient: "from-cyan-600 to-teal-600",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <rect x="2" y="3" width="20" height="4" rx="1" />
        <rect x="2" y="10" width="20" height="4" rx="1" />
        <rect x="2" y="17" width="20" height="4" rx="1" />
        <path d="M6 5h.01M6 12h.01M6 19h.01" />
      </svg>
    ),
    title: "Backend Engineering",
    tagline: "Node.js · Python · Go · PostgreSQL",
    desc: "Scalable REST and GraphQL APIs, microservices, and database architectures built for millions of users. We design for 10x your projected peak traffic from the start.",
    features: ["REST & GraphQL APIs", "Microservices architecture", "Real-time WebSocket systems", "Database design & optimization", "Auth, RBAC & security", "Load testing & performance"],
    href: "/services/backend-engineering",
    color: "from-emerald-500/20 to-emerald-600/5",
    accent: "text-emerald-400",
    border: "border-emerald-500/20",
    gradient: "from-emerald-600 to-green-600",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z" />
      </svg>
    ),
    title: "Cloud Solutions",
    tagline: "AWS · GCP · Azure · Terraform · Kubernetes",
    desc: "Cloud migrations, infrastructure-as-code, and zero-downtime CI/CD pipelines. Typical clients reduce cloud spend by 25–40% in the first quarter.",
    features: ["Cloud migration (AWS/GCP/Azure)", "Infrastructure as Code (Terraform)", "Kubernetes & container orchestration", "CI/CD pipeline automation", "FinOps & cost optimization", "SOC 2 / HIPAA / PCI compliance"],
    href: "/services/cloud-solutions",
    color: "from-sky-500/20 to-sky-600/5",
    accent: "text-sky-400",
    border: "border-sky-500/20",
    gradient: "from-sky-600 to-blue-600",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
        <circle cx="8.5" cy="7.5" r="1" fill="currentColor" />
        <circle cx="13.5" cy="6.5" r="1" fill="currentColor" />
      </svg>
    ),
    title: "UI/UX Design",
    tagline: "Figma · Design Systems · User Research",
    desc: "Human-centered design that converts. We prototype, test with real users, and iterate — resulting in interfaces that are beautiful, intuitive, and measurably effective.",
    features: ["User research & journey mapping", "Information architecture", "High-fidelity Figma prototypes", "Brand & visual design systems", "Design-to-dev handoff", "Usability testing & A/B testing"],
    href: "/services/uiux-design",
    color: "from-rose-500/20 to-rose-600/5",
    accent: "text-rose-400",
    border: "border-rose-500/20",
    gradient: "from-rose-600 to-pink-600",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <rect x="4" y="8" width="16" height="10" rx="2" />
        <path d="M8 8V6a4 4 0 0 1 8 0v2" />
        <circle cx="9" cy="13" r="1.5" fill="currentColor" stroke="none" />
        <circle cx="15" cy="13" r="1.5" fill="currentColor" stroke="none" />
      </svg>
    ),
    title: "AI & Automation",
    tagline: "LLMs · RAG · AI Agents · Workflow Automation",
    desc: "Production-grade AI integrations, intelligent agents, and workflow automation that eliminate manual work and give your product a genuine edge in the market.",
    features: ["LLM integrations (GPT-4, Claude)", "RAG systems over your data", "Multi-step AI agents", "Workflow & process automation", "ML pipelines & model serving", "AI safety & guardrails"],
    href: "/services/ai-automation",
    color: "from-amber-500/20 to-amber-600/5",
    accent: "text-amber-400",
    border: "border-amber-500/20",
    gradient: "from-amber-600 to-orange-600",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <path d="M2 12l1.5-5A2 2 0 0 1 5.4 5.5H14a2 2 0 0 1 2 2V16H8" />
        <path d="M16 9h3.2a2 2 0 0 1 1.7 1l1.1 2v4h-2" />
        <circle cx="6.5" cy="16.5" r="1.8" />
        <circle cx="17.5" cy="16.5" r="1.8" />
        <path d="M9 8.5v3M7.5 10h3" />
      </svg>
    ),
    title: "ClaimGuard NEMT",
    tagline: "Dispatch · Driver App · Medicaid Billing",
    desc: "Purpose-built software for Non-Emergency Medical Transportation providers. Schedule trips, optimize routes, run your driver app, and turn completed rides into paid Medicaid claims — all in one platform.",
    features: ["Trip scheduling & standing orders", "Real-time dispatch & routing", "Driver mobile app with e-signatures", "Medicaid billing & EDI 837 claims", "Broker integrations (Modivcare, MTM)", "HIPAA-compliant records & reporting"],
    href: "/services/nemt-solutions",
    color: "from-emerald-500/20 to-emerald-600/5",
    accent: "text-emerald-400",
    border: "border-emerald-500/20",
    gradient: "from-emerald-600 to-teal-600",
  },
  /* Security Guard Solutions — temporarily hidden from the site (kept for later re-enable)
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8">
        <path d="M12 2l7 4v5c0 4.418-3.134 8.566-7 9.95C8.134 19.566 5 15.418 5 11V6l7-4z" />
        <path d="M9 11l2 2 4-4" />
      </svg>
    ),
    title: "Security Guard Solutions",
    tagline: "Scheduling · Patrol Tracking · Billing",
    desc: "An all-in-one platform for security guard companies. Schedule guards, prove patrols with GPS and checkpoints, capture incidents in the field, and turn verified hours into accurate billing and payroll.",
    features: ["Guard scheduling & rostering", "GPS patrol & tour tracking", "Incident reporting with photos", "Geofenced time & attendance", "Automated billing & payroll export", "Branded client proof-of-service portal"],
    href: "/services/security-guard-solutions",
    color: "from-blue-500/20 to-blue-600/5",
    accent: "text-blue-400",
    border: "border-blue-500/20",
    gradient: "from-blue-600 to-indigo-600",
  },
  */
];

const WHY_US = [
  { title: "No offshore surprises", desc: "US-based project management with transparent async communication. You always know what's being built and why." },
  { title: "Agile with real previews", desc: "Two-week sprints, deployed previews every cycle. You review working software — not screenshots." },
  { title: "Built to last", desc: "We write code for the engineer who comes after us. No shortcuts, no tech debt landmines." },
  { title: "Full-stack team", desc: "Design, frontend, backend, mobile, cloud, and AI — all under one roof, all coordinated by one PM." },
];

export default function ServicesPage() {
  useSEO({
    title: "Software Dev Services | Web, Mobile & AI | BerryByte",
    description: "Full-stack software development — web apps, mobile, backend engineering, cloud solutions, UI/UX & AI automation for US & Canadian businesses.",
    keywords: "software development services, custom web development, mobile app development agency, backend engineering services, cloud solutions company, UI UX design agency, AI automation services US Canada",
    canonical: "https://www.berrybyteinn.com/services",
  });

  return (
    <SharedLayout>
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden pt-20">
        <GlowOrb className="w-[700px] h-[700px] bg-cyan-600 -top-32 -right-40" />
        <GlowOrb className="w-[500px] h-[500px] bg-violet-700 bottom-0 -left-32" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <a href="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-cyan-400 text-sm font-medium transition-colors duration-200 mb-8">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            Home / Services
          </a>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-sm font-medium mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
              6 Disciplines · One Team · End-to-End Delivery
            </div>

            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6">
              Everything you need
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-violet-400 to-emerald-400 bg-clip-text text-transparent">
                to ship and scale.
              </span>
            </h1>

            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl">
              From product strategy to production deployment, we have every engineering discipline under one roof — coordinated by a single, accountable team.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="relative py-20 bg-[#070b14]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-8">
          {SERVICES.map((svc, i) => {
            const Icon = svc.icon;
            return (
              <div
                key={svc.title}
                className={`group relative rounded-3xl border ${svc.border} bg-gradient-to-br ${svc.color} overflow-hidden hover:-translate-y-1 hover:shadow-2xl transition-all duration-300`}
              >
                <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${svc.gradient}`} />
                <div className="p-8 lg:p-10">
                  <div className="grid lg:grid-cols-2 gap-10 items-start">
                    <div>
                      <div className={`inline-flex p-3 rounded-xl bg-white/5 border border-white/10 ${svc.accent} mb-5`}>
                        <Icon />
                      </div>
                      <div className="text-xs text-slate-500 font-medium tracking-wider mb-2">{svc.tagline}</div>
                      <h2 className="text-3xl font-black text-white mb-4">{svc.title}</h2>
                      <p className="text-slate-400 leading-relaxed mb-6">{svc.desc}</p>
                      <Link
                        to={svc.href}
                        className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r ${svc.gradient} text-white text-sm font-semibold hover:-translate-y-0.5 transition-all duration-200 shadow-lg`}
                      >
                        Learn More
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {svc.features.map((f) => (
                        <div key={f} className="flex items-center gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                          <span className={`flex-shrink-0 w-5 h-5 rounded-full bg-white/10 border border-white/10 flex items-center justify-center ${svc.accent}`}>
                            <Check />
                          </span>
                          <span className="text-slate-300 text-xs font-medium">{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="relative py-28 bg-[#050810] overflow-hidden">
        <GlowOrb className="w-[600px] h-[600px] bg-violet-700 top-1/2 -translate-y-1/2 -left-32" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-violet-400 font-semibold text-sm tracking-widest uppercase">Why BerryByte</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">
              The agency difference
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_US.map((item) => (
              <div key={item.title} className="p-6 rounded-2xl border border-white/5 bg-white/[0.02] hover:-translate-y-1 transition-transform duration-300">
                <div className="w-10 h-10 rounded-xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center text-violet-400 mb-4">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-5 h-5">
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-white font-bold mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[700px] h-[700px] bg-violet-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-black text-white mb-6">Ready to get started?</h2>
          <p className="text-slate-400 text-lg mb-8">Tell us about your project and we'll match you with the right team.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-cyan-600 text-white font-semibold hover:from-violet-500 hover:to-cyan-500 hover:-translate-y-1 transition-all duration-300 shadow-2xl shadow-violet-900/40">
              Start a Project
            </Link>
            <a href="tel:+12018469830" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              Call +1 201-846-9830
            </a>
          </div>
        </div>
      </section>
    </SharedLayout>
  );
}
